import {aspectFit, centerSize, loadTexture, UserPic} from "@dp-instant/2d";
import {Action, di, lazyInject} from "@dp-instant/core";
import {Localize} from "@dp-instant/i18n";
import {Types2D, TypesSocial} from "@dp-instant/interfaces";
import {ISocial, IUser} from "@dp-instant/social";
import {GenerateMapSpriteAction} from "@game/coregame/actions/GenerateMapSpriteAction";
import {ScreenShotActionSIOPayload} from "@game/coregame/actions/ScreenShotActionSIO";
import {GameConstants} from "@game/coregame/constants";
import {GameModel} from "@game/coregame/model/GameModel";
import Root from "@game/coregame/view/Root";
import {TypesGame} from "@game/TypesGame";
import {inject, injectable} from "inversify";
import {
    BitmapText,
    BLEND_MODES,
    Container,
    Graphics,
    RenderTexture,
    Sprite,
    Texture,
    utils
} from "pixi.js-legacy";

type ShareImagePayload = {points: number};
export type LevelCompletedSharedImageData = {
    image?: string;
};

const {subTitleFontStyle, scoreFontStyle, scoreBarStyle, layoutSettings} =
    GameConstants.LevelCompletedScreenShot;

@injectable()
export class GenerateShareImageAction extends Action<
    ShareImagePayload,
    LevelCompletedSharedImageData
> {
    @inject(Types2D.rootView)
    protected rootView: Root;

    @inject(TypesSocial.model)
    protected social: ISocial;

    @inject(TypesGame.model)
    protected model: GameModel;

    @lazyInject(Types2D.screenShotAction)
    protected screenshotAction: Action<ScreenShotActionSIOPayload>;

    async execute({points}: ShareImagePayload): Promise<LevelCompletedSharedImageData> {
        let screenCont: Container = await this.drawScreenShot(points, this.social.me);
        const image = this.rootView.app.renderer.extract.base64(screenCont);

        return {image};
    }

    public async drawScreenShot(points: number, user: IUser): Promise<Container> {
        const [avatarCont, map] = await Promise.all([
            this.createAvatar(points, user),
            this.createFieldSprite()
        ]);
        const bg = new Graphics().beginFill(0xd0d0d0).drawRect(0, 0, 10, 10).endFill();
        const overlay = new Graphics().beginFill(0, 0.2).drawRect(0, 0, 10, 10).endFill();
        const finalImage = new Container();
        const bounds = map.getBounds().pad(20, 20);
        finalImage.addChild(bg, map, avatarCont);

        overlay.width = bg.width = bounds.width;
        overlay.height = bg.height = bounds.height;

        map.x = (bounds.width - map.width) * 0.5;
        map.y = (bounds.height - map.height) * 0.5;
        avatarCont.x = (bounds.width - layoutSettings.avatarWidth) * 0.5;
        avatarCont.y = (bounds.height - layoutSettings.avatarHeight) * 0.5;
        const options = {width: bounds.width, height: bounds.height};
        const renderTexture = RenderTexture.create(options);
        this.rootView.app.renderer.render(avatarCont, renderTexture);
        const avatarCropped = new Sprite(renderTexture);
        Texture.removeFromCache(renderTexture);
        finalImage.removeChild(avatarCont);
        finalImage.addChild(overlay);
        finalImage.addChild(avatarCropped);
        return finalImage;
    }

    protected async createFieldSprite() {
        return await di.get<GenerateMapSpriteAction>(TypesGame.actions.createMap).run({
            activeStage: Number.MAX_SAFE_INTEGER,
            data: this.model.currentContinent.data,
            area: {width: 640, height: 640}
        });
    }

    protected async createAvatar(points: string | number, user: IUser | undefined) {
        const avatarCont = new Container();
        const maskedImageCont = new Container();
        const userPic = new UserPic();
        const R = layoutSettings.avatarRadius;
        userPic.width = layoutSettings.avatarWidth;
        userPic.height = layoutSettings.avatarHeight;
        maskedImageCont.addChild(userPic);
        if (user) {
            let texture = await loadTexture(user.photo);
            if (texture) {
                texture.textureCacheIds[0] = `user-photo-${user.id}`;
                utils.TextureCache[user.photo] = texture;
            }
        }
        const framing = new Sprite(utils.TextureCache["victory-framing.svg"]);
        framing.anchor.set(0.5, 1);
        framing.x = layoutSettings.avatarWidth * 0.5;
        framing.y = layoutSettings.avatarHeight + 40;

        const rays = new Sprite(utils.TextureCache["animation/win-rays.svg"]);
        rays.anchor.set(0.5);
        rays.blendMode = BLEND_MODES.OVERLAY;
        rays.width = layoutSettings.maxWidth;
        rays.alpha = 0.6;
        aspectFit({width: 540, height: 774}, rays);
        rays.x = layoutSettings.avatarWidth * 0.5;
        rays.y = layoutSettings.avatarHeight * 0.5;

        userPic.user = user;

        const circleMask = new Graphics().beginFill(0xffffff).drawCircle(R, R, R).endFill();
        maskedImageCont.addChild(circleMask);
        maskedImageCont.mask = circleMask;
        // maskedImageCont.y = layoutSettings.avatarHeight * 0.5;

        // score badge
        const label = new BitmapText("" + points, {...scoreFontStyle, fontName: "Helvetica"});
        const bar: Graphics = new Graphics();
        const scoreBadge = new Container();
        scoreBadge.addChild(bar, label);
        scoreBadge.y = layoutSettings.badgeOffsetY;

        label.text = "" + points;

        const {color, width, height, radius} = scoreBarStyle;

        bar.clear().beginFill(color).drawRoundedRect(0, 0, width, height, radius);
        centerSize(bar, label);
        //
        const subtitle = Localize.get("level_completed_message", "ALL STAGES COMPLETED!");

        const subTitleText = new BitmapText(subtitle, {
            ...subTitleFontStyle,
            fontName: "Helvetica"
        });
        subTitleText.x = (layoutSettings.avatarWidth - subTitleText.width) * 0.5;
        subTitleText.y = layoutSettings.subtitleOffsetY;

        avatarCont.addChild(rays);
        avatarCont.addChild(maskedImageCont);
        avatarCont.addChild(framing);
        avatarCont.addChild(subTitleText);
        avatarCont.addChild(scoreBadge);

        Texture.removeFromCache(userPic.sprite?.texture);

        return avatarCont;
    }
}
